import styles from './errorPage.module.scss';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';
import { AlloyButton, BreadCrumb, AlloyIcon } from 'cat-ecommerce-alloy';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import WidgetRow from './WidgetRow/WidgetRow';
import { imageData } from '../constant';
import { isExternalReferrer } from '@app/utils';
import Conditional from '@app/components/common/Conditional/Conditional';
import CriticalErrorMessage from '@app/components/common/CriticalErrorMessage';
import useAnalytics from '@app/hooks/useAnalytics';
import { useCallback, useEffect, useMemo } from 'react';
import { EVENT_CRITICAL_ERROR } from '@app/constants/analyticsConstants';
import { ADP_FAIL_REGISTRATION, LOGIN } from '@app/constants/errorConstants';
import { PCC_RELEASE_REG_AND_LOGIN } from '@app/constants/featureFlags';

const ErrorPage = () => {
  const { t } = useTranslation();
  const { fireErrorTrackingEvent } = useAnalytics();
  const isPCC_CustomerSupportErrorEventLoggingRegAndLogin = useSelector(
    s => s.featureFlag?.[PCC_RELEASE_REG_AND_LOGIN]
  );
  const dealerUrl = useSelector(s => s.dealer.seoURL);
  let history = useHistory();
  const data = [{ label: t('BCT_HOME'), value: dealerUrl }];

  const showBackButtonAndBreadCrumb = !isExternalReferrer(
    document.referrer,
    document.location.host
  );

  const checkB2CError = useCallback(
    path =>
      isPCC_CustomerSupportErrorEventLoggingRegAndLogin &&
      window.location.href.includes(path),
    [isPCC_CustomerSupportErrorEventLoggingRegAndLogin]
  );

  const isB2CLoginError = useMemo(
    () => checkB2CError('b2c-login'),
    [checkB2CError]
  );
  const isB2CRegistrationError = useMemo(
    () => checkB2CError('b2c-registration'),
    [checkB2CError]
  );

  useEffect(() => {
    if (isB2CLoginError || isB2CRegistrationError) {
      const translatedValue = t(LOGIN);
      const failureName = isB2CLoginError ? LOGIN : ADP_FAIL_REGISTRATION;

      // On initial render, the translation value is empty (""); causing an empty ("") value to be passed to GA.
      // So only fire the event when the translation is loaded.
      if (translatedValue) {
        fireErrorTrackingEvent(EVENT_CRITICAL_ERROR, '404', failureName);
      }
    }
  }, [isB2CLoginError, isB2CRegistrationError, fireErrorTrackingEvent, t]);

  return (
    <div>
      <div className={styles[`error-page__header`]}>
        <Conditional test={showBackButtonAndBreadCrumb}>
          <BreadCrumb
            links={data}
            backButtonText={t('UC_BACK')}
            showBackButton
            hideLastLink
          />
        </Conditional>
      </div>
      <div
        className={cx(
          styles['error-page'],
          'd-flex justify-content-center align-items-center'
        )}
      >
        <div className="w-100">
          <div className={styles['error-page__top']}>
            <div
              className={cx(
                styles['error-page__top-upper'],
                'd-flex justify-content-center align-items-center flex-column'
              )}
            >
              {isB2CLoginError || isB2CRegistrationError ? (
                <div className="mt-3">
                  <CriticalErrorMessage />
                </div>
              ) : (
                <>
                  <h1 className="h2 display-2 text-center fw-bold">
                    {t('OOPS_GENERIC_ERROR_404PAGE')}
                  </h1>
                  <p className="u-color--DarkGray u-fs--16 text-sans-serif	fw-normal text-center">
                    {t('DETAILS_OOPS_GENERIC_ERROR_404PAGE')}
                  </p>
                </>
              )}
              <div
                className={cx(
                  styles['error-page__buttons'],
                  'mb-3 d-flex flex-column-reverse flex-md-row align-items-start p-0  justify-content-center'
                )}
              >
                <Conditional test={showBackButtonAndBreadCrumb}>
                  <AlloyButton
                    data-testid="back-btn"
                    className={cx(
                      styles['btn-back'],
                      'd-flex justify-content-center align-items-center'
                    )}
                    onClick={() => history.goBack()}
                    icon={
                      <AlloyIcon
                        icon="icon-chevron-left"
                        style={{
                          width: '16px',
                          height: '16px',
                          top: '8px',
                          left: '15.5px'
                        }}
                      />
                    }
                    buttonType="tertiary"
                  >
                    {t('UC_BACK')}
                  </AlloyButton>
                </Conditional>
                <a
                  href={dealerUrl || `/webapp/wcs/stores/servlet/en`}
                  className={cx('col-12 col-md-auto p-0')}
                >
                  <AlloyButton
                    buttonType="primary"
                    className={cx(styles['btn-parts'])}
                  >
                    {t('GO_TO_PARTS')}
                  </AlloyButton>
                </a>
              </div>
            </div>
            <div className={cx(styles['error-page__404-illustration'], 'mb-5')}>
              <img
                className={cx(styles['error-page__404-image'], 'h-100 w-100')}
                src={imageData.imageSrc}
                alt={imageData.alt}
              />
            </div>
          </div>
          <div className={cx(styles['error-page__bottom'])}>
            <div
              className={cx(
                styles['error-page__bottom-text-container'],
                'text-center'
              )}
            >
              <h3
                className={cx(
                  styles['error-page__bottom-header'],
                  'u-fs--24 text-center'
                )}
              >
                {t('LOOKING_FOR_HELP')}
              </h3>
              <p className="u-color--DarkGray u-fs--16 text-sans-serif fw-normal pb-2 text-center">
                {t('LOOKING_FOR_HELP_DETAILS')}
              </p>
            </div>
            <WidgetRow />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorPage;
